import axios from "axios";

import type {
  AppState,
  Operation,
  SyncRequest as SyncReq,
  SyncResponse as SyncResp,
  UserPreferences,
} from "@/lib/types";

const apiClient = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

export interface SyncResponse {
  state: AppState;
  preferences: UserPreferences & { activeListId?: string | null };
  lastSynced?: string;
}

export interface SyncRequest {
  state: AppState;
  preferences?: UserPreferences;
}

export const syncApi = {
  // Fetch user data from database (initial load only)
  fetchSync: async (): Promise<SyncResponse> => {
    const response = await apiClient.get<SyncResponse>("/sync");
    return response.data;
  },

  // Sync operations (new operation-based sync)
  syncOperations: async (
    operations: Operation[],
    lastSyncedAt: string | null
  ): Promise<SyncResp> => {
    const response = await apiClient.post<SyncResp>("/sync", {
      operations,
      lastSyncedAt,
    } as SyncReq);
    return response.data;
  },

  // Legacy: Save full state (kept for backward compatibility)
  saveSync: async (data: SyncRequest): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>("/sync/legacy", data);
    return response.data;
  },
};

export const preferencesApi = {
  // Fetch user preferences from database
  fetchPreferences: async (): Promise<UserPreferences & { activeListId?: string | null }> => {
    const response = await apiClient.get<UserPreferences & { activeListId?: string | null }>(
      "/preferences"
    );
    return response.data;
  },

  // Update user preferences in database
  updatePreferences: async (
    preferences: Partial<UserPreferences> & { activeListId?: string | null }
  ): Promise<UserPreferences & { activeListId?: string | null }> => {
    const response = await apiClient.put<UserPreferences & { activeListId?: string | null }>(
      "/preferences",
      preferences
    );
    return response.data;
  },
};

export default apiClient;
